/**
 * design_handoff_auth §2 — the log-in split layout's right pane. Fetches
 * /changelog.json on mount so a release note can ship without a deploy.
 * Loading -> four skeleton rows in the final shape so the pane doesn't
 * reflow. Fetch failure -> the pane's own error block with a retry button;
 * this must never block logging in, so failures never bubble further.
 */
import React, { useCallback, useEffect, useState } from "react";
import classNames from "classnames";
import { Badge, Icon } from "../../theme/untitled";
import styles from "./WhatsNewPane.module.scss";

const BADGE_BY_KIND = {
  new: { color: "success", label: "New" },
  improved: { color: "blueLight", label: "Improved" },
  fixed: { color: "gray", label: "Fixed" },
};

const SKELETON_ROW_COUNT = 4;

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) {
    return dateStr;
  }
  return date.toLocaleDateString(window.navigator.language || "en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const WhatsNewPane = () => {
  const [status, setStatus] = useState("loading"); // loading | success | error
  const [entries, setEntries] = useState([]);

  const load = useCallback(() => {
    setStatus("loading");
    fetch("/changelog.json")
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to load changelog");
        }
        return response.json();
      })
      .then((data) => {
        setEntries(Array.isArray(data) ? data : []);
        setStatus("success");
      })
      .catch(() => {
        setStatus("error");
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (status === "error") {
    return (
      <div className={styles.pane}>
        <div className={styles.errorState}>
          <span className={styles.errorDisc}>
            <Icon name="AlertCircle" size={20} />
          </span>
          <h2 className={styles.errorTitle}>Updates aren't loading</h2>
          <p className={styles.errorBody}>
            We couldn't reach the changelog. This doesn't affect your
            account — log in as normal.
          </p>
          <button type="button" className={styles.retryBtn} onClick={load}>
            <Icon name="RefreshCw02" size={16} />
            Try again
          </button>
        </div>
      </div>
    );
  }

  const skeletonRows = Array.from({ length: SKELETON_ROW_COUNT }).map((_, idx) => (
    <li key={idx} className={styles.item}>
      <div className={styles.itemTop}>
        <span className={classNames(styles.skeleton, styles.skeletonBadge)} />
        <span className={classNames(styles.skeleton, styles.skeletonDate)} />
      </div>
      <span className={classNames(styles.skeleton, styles.skeletonTitle)} />
      <span className={classNames(styles.skeleton, styles.skeletonLine1)} />
      <span className={classNames(styles.skeleton, styles.skeletonLine2)} />
    </li>
  ));

  const entryRows = entries.map((entry, idx) => {
    const badge = BADGE_BY_KIND[entry.kind] || BADGE_BY_KIND.fixed;
    const moreLink = entry.url ? (
      <a className={styles.more} href={entry.url} target="_blank" rel="noreferrer">
        {entry.label || "Read more"}
        <Icon name="ArrowRight" size={16} />
      </a>
    ) : null;

    return (
      <li key={idx} className={styles.item}>
        <div className={styles.itemTop}>
          <Badge color={badge.color}>{badge.label}</Badge>
          <span className={styles.date}>{formatDate(entry.date)}</span>
        </div>
        <b className={styles.itemTitle}>{entry.title}</b>
        <p className={styles.itemBody}>{entry.body}</p>
        {moreLink}
      </li>
    );
  });

  const rows = status === "loading" ? skeletonRows : entryRows;

  return (
    <div className={styles.pane} aria-busy={status === "loading" || undefined}>
      <div className={styles.head}>
        <h2>What's new</h2>
        <span className={styles.kicker}>Product updates</span>
      </div>
      <ul className={styles.list}>{rows}</ul>
    </div>
  );
};

export default WhatsNewPane;
